import { openDB, IDBPDatabase } from "idb"

export const MAX_MEMORY_BYTES = 64 * 1024 * 1024
export const MAX_PERSISTED_BYTES = 16 * 1024 * 1024
export const MAX_PERSISTED_TOTAL_BYTES = 128 * 1024 * 1024
export const PREVIEW_CACHE_TTL_MS = 60 * 60 * 1000

export type PreviewCacheKey = readonly [
    accountScope: string,
    fileId: string,
    revision: string,
    representation: "original" | "light",
    page: number | null,
    language?: string,
    recipeVersion?: string,
]

type MemoryEntry = { blob: Blob; lastAccess: number }
type PersistedEntry = MemoryEntry & { key: string }
type InFlightEntry = {
    controller: AbortController
    consumers: number
    settled: boolean
    promise: Promise<Blob>
}

const releaseInFlight = (serialized: string, entry: InFlightEntry): void => {
    entry.consumers -= 1
    if (entry.consumers === 0 && !entry.settled) {
        entry.controller.abort()
        // Let a new consumer start a fresh request immediately. The identity
        // check in the promise finalizer prevents the aborted request from
        // deleting that replacement entry when it settles later.
        if (inFlight.get(serialized) === entry) inFlight.delete(serialized)
    }
}

const memory = new Map<string, MemoryEntry>()
const inFlight = new Map<string, InFlightEntry>()
let memoryBytes = 0
let epoch = 0
let databasePromise: Promise<IDBPDatabase> | null = null

export const previewCacheKey = (
    accountScope: string,
    fileId: string,
    revision: string,
    representation: "original" | "light",
    page: number | null,
    language = "original",
    recipeVersion = "v1"
): PreviewCacheKey => [accountScope, fileId, revision, representation, page, language, recipeVersion]

const serialize = (key: PreviewCacheKey): string => JSON.stringify(key)

const openDatabase = async (): Promise<IDBPDatabase | null> => {
    if (typeof indexedDB === "undefined") return null
    if (!databasePromise) {
        databasePromise = openDB("aidocr-preview-cache-v1", 1, {
            upgrade(db) {
                if (!db.objectStoreNames.contains("blobs")) {
                    db.createObjectStore("blobs", { keyPath: "key" })
                }
            },
        }).catch(() => null as unknown as IDBPDatabase)
    }
    return databasePromise
}

const evictMemory = (requiredBytes: number): void => {
    while (memoryBytes + requiredBytes > MAX_MEMORY_BYTES && memory.size > 0) {
        const oldest = [...memory.entries()].sort(([, a], [, b]) => a.lastAccess - b.lastAccess)[0]
        if (!oldest) break
        memory.delete(oldest[0])
        memoryBytes -= oldest[1].blob.size
    }
}

const putMemory = (key: string, blob: Blob): void => {
    if (blob.size > MAX_MEMORY_BYTES) return
    const previous = memory.get(key)
    if (previous) memoryBytes -= previous.blob.size
    evictMemory(blob.size)
    memory.set(key, { blob, lastAccess: Date.now() })
    memoryBytes += blob.size
}

const readPersisted = async (key: string): Promise<Blob | null> => {
    try {
        const db = await openDatabase()
        const entry = (await db?.get("blobs", key)) as PersistedEntry | undefined
        if (!entry || Date.now() - entry.lastAccess >= PREVIEW_CACHE_TTL_MS) {
            if (entry) await db?.delete("blobs", key)
            return null
        }
        return entry.blob
    } catch {
        return null
    }
}

const persist = async (key: string, blob: Blob, startedEpoch: number, persistable: boolean): Promise<void> => {
    if (startedEpoch !== epoch || !persistable || blob.size > MAX_PERSISTED_BYTES) return
    try {
        const db = await openDatabase()
        if (!db || startedEpoch !== epoch) return
        const tx = db.transaction("blobs", "readwrite")
        await tx.store.put({ key, blob, lastAccess: Date.now() } satisfies PersistedEntry)
        const entries: { key: string; size: number; lastAccess: number }[] = []
        let cursor = await tx.store.openCursor()
        while (cursor) {
            const entry = cursor.value as PersistedEntry
            if (Date.now() - entry.lastAccess >= PREVIEW_CACHE_TTL_MS) await cursor.delete()
            else entries.push({ key: entry.key, size: entry.blob.size, lastAccess: entry.lastAccess })
            cursor = await cursor.continue()
        }
        let total = entries.reduce((sum, entry) => sum + entry.size, 0)
        for (const entry of entries.sort((a, b) => a.lastAccess - b.lastAccess)) {
            if (total <= MAX_PERSISTED_TOTAL_BYTES) break
            await tx.store.delete(entry.key)
            total -= entry.size
        }
        await tx.done
    } catch {
        // Persistent cache is an optimization; quota and private-mode failures are cache misses.
    }
}

export type PreviewLease = { promise: Promise<Blob>; release: () => void }

export function acquirePreviewBlob(
    key: PreviewCacheKey,
    fetcher: (signal: AbortSignal) => Promise<Blob>
): PreviewLease {
    const serialized = serialize(key)
    const cached = memory.get(serialized)
    if (cached && Date.now() - cached.lastAccess < PREVIEW_CACHE_TTL_MS) {
        cached.lastAccess = Date.now()
        return { promise: Promise.resolve(cached.blob), release: () => undefined }
    }

    if (cached) {
        memory.delete(serialized)
        memoryBytes -= cached.blob.size
    }
    const existing = inFlight.get(serialized)
    if (existing) {
        existing.consumers += 1
        let released = false
        return {
            promise: existing.promise,
            release: () => {
                if (released) return
                released = true
                releaseInFlight(serialized, existing)
            },
        }
    }

    const controller = new AbortController()
    const startedEpoch = epoch
    const entry = {} as InFlightEntry
    entry.controller = controller
    entry.consumers = 1
    entry.settled = false
    entry.promise = (async () => {
        const persisted = await readPersisted(serialized)
        if (persisted) {
            if (startedEpoch === epoch && !controller.signal.aborted) putMemory(serialized, persisted)
            return persisted
        }
        const blob = await fetcher(controller.signal)
        if (startedEpoch === epoch && !controller.signal.aborted) {
            putMemory(serialized, blob)
            // Full-document resources stay network/range-backed. Only selected
            // page resources are persisted so IndexedDB cannot become a second
            // unbounded full-document cache.
            void persist(serialized, blob, startedEpoch, key[4] !== null)
        }
        return blob
    })().finally(() => {
        entry.settled = true
        if (inFlight.get(serialized) === entry) inFlight.delete(serialized)
    })
    inFlight.set(serialized, entry)

    let released = false
    return {
        promise: entry.promise,
        release: () => {
            if (released) return
            released = true
            releaseInFlight(serialized, entry)
        },
    }
}

export function clearPreviewCache(): void {
    epoch += 1
    memory.clear()
    memoryBytes = 0
    for (const entry of inFlight.values()) entry.controller.abort()
    inFlight.clear()
    void openDatabase()
        .then(db => db?.clear("blobs"))
        .catch(() => undefined)
}
